import {AlignItems, JustifyContent, Padding, RowDirection} from '@gongxigames/framework/core/fragments/display/FlexBox/flexBoxTypes';
import {FlexInnerItem, IFlexInnerItem} from '@gongxigames/framework/core/fragments/display/FlexInnerItem';
import {Bounds, Rectangle} from 'pixi.js';

export type FlexBoxLayoutOptions = {
    wrap: boolean;
    gap?: number;
    direction: RowDirection;
    justifyContent: JustifyContent;
    alignItems: AlignItems;
    padding: Padding;
    containerWidth?: number;
    containerHeight?: number;
};

export type FlexRow = {
    sizeByMainAxis: number;
    sizeByCrossAxis: number;
    positionOnMainAxis: number;
    positionOnCrossAxis: number;
    amountOfItems: number;
};

type FlexBoxAxisPadding = {first: number; second: number;};

export class FlexBoxLayout {
    protected options!: FlexBoxLayoutOptions;
    protected items!: IFlexInnerItem[];
    protected isRowDirection!: boolean;
    protected itemsBoundsWidth = 0;
    protected itemsBoundsHeight = 0;
    protected parameters!: {
        mainAxisSize: number;
        crossAxisSize: number;
        mainAxisPadding: FlexBoxAxisPadding;
        crossAxisPadding: FlexBoxAxisPadding;
    };
    protected layout: FlexRow[] = [{
        sizeByMainAxis: 0,
        sizeByCrossAxis: 0,
        amountOfItems: 0,
        positionOnMainAxis: 0,
        positionOnCrossAxis: 0
    }];

    constructor(options: FlexBoxLayoutOptions, items: Rectangle[]) {
        this.setProperties(options, items);
    }

    protected setProperties(options: FlexBoxLayoutOptions, items: Rectangle[]): void {
        const {direction, containerWidth, containerHeight, padding} = options;

        this.itemsBoundsWidth = !containerWidth ? 0 : containerWidth - (padding.left + padding.right);
        this.itemsBoundsHeight = !containerHeight ? 0 : containerHeight - (padding.top + padding.bottom);

        this.isRowDirection = direction === 'row';
        this.options = options;
        this.items = this.getFlexBoxInnerItems(items, direction);
        this.parameters = {
            mainAxisSize: this.isRowDirection ? this.itemsBoundsWidth : this.itemsBoundsHeight,
            crossAxisSize: this.isRowDirection ? this.itemsBoundsHeight : this.itemsBoundsWidth,
            mainAxisPadding: {
                first: this.isRowDirection ? padding.left : padding.top,
                second: this.isRowDirection ? padding.right : padding.bottom
            },
            crossAxisPadding: {
                first: this.isRowDirection ? padding.top : padding.left,
                second: this.isRowDirection ? padding.bottom : padding.right
            }
        };
    }

    protected getFlexBoxInnerItems(items: Rectangle[], direction: RowDirection): IFlexInnerItem[] {
        return items.map(item => new FlexInnerItem(direction, item.width, item.height));
    }

    public update(options: FlexBoxLayoutOptions, items: Rectangle[]): void {
        this.setProperties(options, items);
        this.clearLayout();
        this.createLayout();
    }

    public isOptionsEqual(options: FlexBoxLayoutOptions): boolean {
        return (Object.keys(options) as (keyof FlexBoxLayoutOptions)[]).every(key => {
            if (key === 'padding') {
                return this.isPaddingEqual(options[key]);
            }

            return this.options[key] === options[key];
        });
    }

    protected isPaddingEqual(padding: Padding): boolean {
        const {left, right, top, bottom} = this.options.padding;

        return padding.left === left && padding.right === right && padding.top === top && padding.bottom === bottom;
    }

    protected clearLayout(): void {
        this.layout = [{
            sizeByMainAxis: 0,
            sizeByCrossAxis: 0,
            amountOfItems: 0,
            positionOnMainAxis: 0,
            positionOnCrossAxis: 0
        }];
    }

    protected createLayout(): void {
        this.createFlexRows();
        this.alignFlexRows();
        this.assignCoordinates();
    }

    protected createFlexRows(): void {
        const {layout, options, items} = this;
        const {wrap, gap = 0} = options;
        const containerLength = this.isRowDirection ? this.itemsBoundsWidth : this.itemsBoundsHeight;

        items.forEach((item: IFlexInnerItem, index: number) => {
            const currentRow = layout[layout.length - 1];
            const itemSizeByAxis = item.sizeByAxis;
            const isLastItem = items.length - 1 === index;
            const newLength = currentRow.sizeByMainAxis + itemSizeByAxis.main;
            const shouldNotWrap = newLength < containerLength || !wrap || currentRow.sizeByMainAxis === 0;

            if (shouldNotWrap) {
                currentRow.sizeByMainAxis = newLength + (isLastItem ? 0 : gap);
                currentRow.amountOfItems += 1;

                if (itemSizeByAxis.cross > currentRow.sizeByCrossAxis) {
                    currentRow.sizeByCrossAxis = itemSizeByAxis.cross;
                }

            } else {
                currentRow.sizeByMainAxis -= gap;
                layout.push({
                    sizeByMainAxis: itemSizeByAxis.main + (isLastItem ? 0 : gap),
                    sizeByCrossAxis: itemSizeByAxis.cross,
                    amountOfItems: 1,
                    positionOnMainAxis: 0,
                    positionOnCrossAxis: 0
                });
            }
        });
    }

    protected alignFlexRows(): void {
        this.layout.forEach((currentRow: FlexRow, i: number) => {
            currentRow.positionOnMainAxis = this.getRowPositionOnMainAxis(currentRow);
            currentRow.positionOnCrossAxis = this.getRowPositionOnCrossAxis(currentRow, i);
        });
    }

    protected getRowPositionOnMainAxis(row: FlexRow): number {
        const {justifyContent} = this.options;
        const {mainAxisSize, mainAxisPadding} = this.parameters;

        if (justifyContent === 'start' || mainAxisSize === 0) {
            return mainAxisPadding.first;
        } else if (justifyContent === 'center') {
            return ((mainAxisSize - row.sizeByMainAxis) / 2) + mainAxisPadding.first;
        } else {
            return (mainAxisSize - row.sizeByMainAxis) + mainAxisPadding.second;
        }
    }

    protected getRowPositionOnCrossAxis(row: FlexRow, rowIndex: number): number {
        const {alignItems, gap = 0} = this.options;
        const {crossAxisSize, crossAxisPadding} = this.parameters;
        let contentSize = -gap;
        let rowOffset = 0;

        this.layout.forEach((currentRow: FlexRow, i: number) => {
            contentSize += currentRow.sizeByCrossAxis + gap;
            rowOffset += rowIndex > i ? currentRow.sizeByCrossAxis + gap : 0;
        });

        if (alignItems === 'start' || crossAxisSize === 0) {
            return crossAxisPadding.first + rowOffset;
        } else if (alignItems === 'center') {
            return (crossAxisSize / 2 - contentSize / 2 + rowOffset) + crossAxisPadding.first;
        } else {
            const positionOnCrossAxis = crossAxisSize - (contentSize + rowOffset) + crossAxisPadding.second;

            return Math.max(0, positionOnCrossAxis);
        }
    }

    protected assignCoordinates(): void {
        const {items, options, layout} = this;
        const {gap = 0} = options;
        let rowCounter = 0;
        let itemCounter = 0;
        let innerLengthCounter = 0;

        items.forEach((item: IFlexInnerItem) => {
            if (itemCounter > layout[rowCounter].amountOfItems - 1) {
                rowCounter++;
                itemCounter = 0;
                innerLengthCounter = 0;
            }
            const mainAxisPosition = layout[rowCounter].positionOnMainAxis + innerLengthCounter;
            const crossAxisPosition = this.getPositionInsideRow(layout[rowCounter], item);

            item.setPositionByAxis(mainAxisPosition, crossAxisPosition);

            innerLengthCounter += item.sizeByAxis.main + gap;
            itemCounter++;
        });
    }

    protected getPositionInsideRow(row: FlexRow, item: IFlexInnerItem): number {
        const leftoverSpace = row.sizeByCrossAxis - item.sizeByAxis.cross;

        if (this.options.alignItems === 'center') {
            return row.positionOnCrossAxis + leftoverSpace / 2;
        } else if (this.options.alignItems === 'end') {
            return row.positionOnCrossAxis + leftoverSpace;
        }

        return row.positionOnCrossAxis;
    }

    public getContent(): Rectangle[] {
        return this.items.map(item => item.getArea());
    }

    public getBoundsRect(): Rectangle {
        const bounds = new Bounds();
        const {padding} = this.options;

        bounds.addFramePad(0, 0, this.itemsBoundsWidth, this.itemsBoundsHeight, 0, 0);

        this.items.forEach(item => {
            const area = item.getArea();

            bounds.addFramePad(
                area.x,
                area.y,
                area.x + area.width,
                area.y + area.height, 0, 0);
        });

        bounds.maxX += padding.right;
        bounds.maxY += padding.bottom;

        return bounds.getRectangle(new Rectangle);
    }
}
